import { useToolIsMounted } from "#app/sites/site-tool-context";
import { browserEffectRuntime } from "@crm/effect/client/runtime";
import { Button } from "@crm/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@crm/ui/components/ui/dialog";
import { Input } from "@crm/ui/components/ui/input";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Effect, Option } from "effect";
import * as AsyncResult from "effect/unstable/reactivity/AsyncResult";
import {
  ArrowRight,
  Blocks,
  CheckCircle2,
  ChevronRight,
  Globe,
  Image,
  Info,
  Plus,
  Settings2,
} from "lucide-react";
import { useState } from "react";
import { useDesignTemplates } from "#app/design-templates";
import { useAcfContexts, useCreateAcfContext } from "#app/site";
import { useActiveSite } from "#app/sites";

export const Route = createFileRoute("/_protected/site-tools/_layout/")({
  head: () => ({}),
  component: SiteToolsHomePage,
});

type CreateAcfContextMutation = Pick<ReturnType<typeof useCreateAcfContext>, "executePromise">;

type SiteToolsNavigate = ReturnType<typeof useNavigate>;

/** 站点资料完善度：域名 / 业务信息 / 客户信息 三项填充率 */
function siteCompleteness(site: {
  domain: string | null;
  businessInfo: string;
  customerInfo: string;
}): number {
  const filled = [site.domain, site.businessInfo, site.customerInfo].filter(
    (value) => !!value?.trim(),
  ).length;
  return Math.round((filled / 3) * 100);
}

function formatRelativeTime(value: string | Date | null): string {
  if (!value) {
    return "";
  }
  const timestamp = typeof value === "string" ? Date.parse(value) : value.getTime();
  if (!Number.isFinite(timestamp)) {
    return "";
  }
  const diffMs = Date.now() - timestamp;
  const hours = Math.floor(diffMs / 3_600_000);
  if (hours < 1) {
    return "刚刚";
  }
  if (hours < 24) {
    return `${hours} 小时前`;
  }
  if (hours < 48) {
    return "昨天";
  }
  return Intl.DateTimeFormat().format(timestamp);
}

function createSiteToolsProjectEffect(input: {
  readonly name: string;
  readonly createContext: CreateAcfContextMutation;
  readonly navigate: SiteToolsNavigate;
  readonly isMounted: () => boolean;
  readonly setNewName: (name: string) => void;
  readonly setShowCreate: (open: boolean) => void;
  readonly existingContextId?: string;
}): Effect.Effect<void> {
  const trimmed = input.name.trim();
  if (!trimmed) {
    return Effect.void;
  }

  return Effect.gen(function* () {
    const contextId = input.existingContextId
      ? input.existingContextId
      : (yield* Effect.promise(() => input.createContext.executePromise({ name: trimmed }))).id;

    yield* Effect.sync(() => {
      if (!input.isMounted()) return;
      input.setNewName("");
      input.setShowCreate(false);
      void input.navigate({
        to: "/site-tools/acf",
        search: { contextId },
      });
    });
  });
}

function SiteToolsHomePage() {
  const navigate = useNavigate();
  const isMounted = useToolIsMounted();
  const activeSite = useActiveSite();
  const contextsResult = useAcfContexts();
  const contexts = AsyncResult.value(contextsResult).pipe(Option.getOrElse(() => []));
  const isLoadingContexts = AsyncResult.isWaiting(contextsResult);
  const templatesResult = useDesignTemplates();
  const templates = AsyncResult.value(templatesResult).pipe(Option.getOrUndefined);
  const createContext = useCreateAcfContext();
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");

  // 查询已经按站点隔离，历史未归档内容由站点管理页单独处理。
  const siteContexts = (contexts ?? []).filter((ctx) => ctx.siteId === activeSite?.id);
  const inProgressCount = siteContexts.filter((c) => !c.isCompleted).length;
  const resumableContext = siteContexts.find((context) => !context.isCompleted);

  const openCreateOrResume = () => {
    if (resumableContext) {
      void navigate({
        to: "/site-tools/acf",
        search: { contextId: resumableContext.id },
      });
      return;
    }
    setShowCreate(true);
  };

  const handleCreate = () => {
    void browserEffectRuntime.runPromise(
      createSiteToolsProjectEffect({
        name: newName,
        createContext,
        navigate,
        isMounted,
        setNewName,
        setShowCreate,
        existingContextId: resumableContext?.id,
      }),
    );
  };

  return (
    <div className="container mx-auto max-w-6xl px-4 py-8">
      {/* Hero */}
      <div className="mb-7">
        <span className="mb-3.5 inline-flex items-center rounded-full bg-brand-soft px-3 py-1 font-semibold text-[12px] text-brand-text uppercase tracking-widest">
          建站工具
        </span>
        <h1 className="mb-2 font-display font-semibold text-[34px] text-ink leading-tight tracking-tight">
          从<span className="font-display text-brand italic">找感觉</span>到
          <span className="font-display text-brand italic">定结构</span>
          ，为你的站点而建
        </h1>
        <p className="max-w-xl text-[14.5px] text-ink-2 leading-relaxed">
          所有工具都围绕当前站点工作。先逛设计参考找到想要的样子，再用 ACF
          规划把网站数据结构定下来。
        </p>
      </div>

      {/* 当前站点横幅 */}
      {activeSite ? (
        <div className="mb-6 flex flex-wrap items-center gap-5 rounded-2xl border border-border bg-[linear-gradient(120deg,var(--card)_60%,var(--brand-soft))] px-6 py-5 shadow-s">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand to-violet font-bold font-display text-lg text-white">
            {activeSite.name.slice(0, 1)}
          </span>
          <div className="min-w-0">
            <div className="truncate font-bold text-[15.5px] text-ink">{activeSite.name}</div>
            <div className="mt-0.5 flex items-center gap-1.5 text-[12.5px] text-ink-3">
              <Globe size={12} />
              {activeSite.domain ?? "未绑定域名"}
            </div>
          </div>
          <div className="ml-auto flex items-center gap-6">
            <div className="text-right">
              <div className="font-bold font-display text-[17px] text-ink">
                {siteContexts.length}
              </div>
              <div className="text-[11.5px] text-ink-3">ACF 项目</div>
            </div>
            <div className="text-right">
              <div className="font-bold font-display text-[17px] text-ink">
                {siteCompleteness(activeSite)}%
              </div>
              <div className="text-[11.5px] text-ink-3">资料完善度</div>
            </div>
            <Link to="/sites">
              <Button size="sm" variant="outline">
                <Settings2 size={14} />
                站点设置
              </Button>
            </Link>
          </div>
        </div>
      ) : (
        <div className="mb-6 flex flex-wrap items-center gap-4 rounded-2xl border border-warning border-dashed bg-warning-soft px-6 py-5">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-warning/10 text-warning">
            <Globe size={20} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-[14.5px] text-ink">还没有活动站点</div>
            <div className="mt-0.5 text-[12.5px] text-ink-2">
              建站工具围绕站点工作。先创建一个站点，规划项目会归档到对应站点下。
            </div>
          </div>
          <Link search={{ create: true }} to="/sites">
            <Button size="sm">
              <Plus size={14} />
              创建站点
            </Button>
          </Link>
        </div>
      )}

      {/* 双工具卡 */}
      <div className="mb-5 grid grid-cols-1 gap-5 md:grid-cols-2">
        <Link className="group" to="/site-tools/references">
          <div className="group-hover:-translate-y-0.5 relative flex h-full flex-col rounded-2xl border border-border bg-card p-6 transition-all group-hover:shadow-m">
            <span className="absolute top-5 right-6 font-display text-[13px] text-ink-3 italic">
              Step 1
            </span>
            <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-warning-soft text-warning">
              <Image size={21} />
            </span>
            <h3 className="mb-1.5 font-bold text-[17px] text-ink">设计参考</h3>
            <p className="mb-5 text-[13px] text-ink-2 leading-relaxed">
              浏览精选的 B2B 外贸站设计实例——首页、产品页、着陆页都有。找到喜欢的感觉，下载 HTML
              直接套用到你的站点。
            </p>
            <div className="mt-auto flex items-center gap-3">
              <Button size="sm">
                逛逛参考
                <ArrowRight size={14} />
              </Button>
              {templates ? (
                <span className="ml-auto text-[12px] text-ink-3">{templates.length} 个实例</span>
              ) : null}
            </div>
          </div>
        </Link>

        <button className="group text-left" onClick={openCreateOrResume} type="button">
          <div className="group-hover:-translate-y-0.5 relative flex h-full flex-col rounded-2xl border border-border bg-card p-6 transition-all group-hover:shadow-m">
            <span className="absolute top-5 right-6 font-display text-[13px] text-ink-3 italic">
              Step 2
            </span>
            <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-brand-soft text-brand">
              <Blocks size={21} />
            </span>
            <h3 className="mb-1.5 font-bold text-[17px] text-ink">ACF 规划</h3>
            <p className="mb-5 text-[13px] text-ink-2 leading-relaxed">
              和 AI 聊几轮，把"我要展示什么"变成 WordPress ACF 字段配置。生成的 JSON
              可直接导入，省去手写字段的麻烦。
            </p>
            <div className="mt-auto flex items-center gap-3">
              <Button size="sm">
                <Plus size={14} />
                {resumableContext ? "继续规划" : "新建规划"}
              </Button>
              {inProgressCount > 0 ? (
                <span className="ml-auto text-[12px] text-ink-3">
                  {inProgressCount} 个项目进行中
                </span>
              ) : null}
            </div>
          </div>
        </button>
      </div>

      <div className="mb-7 flex items-center gap-2 pl-1 text-[12px] text-ink-3">
        <Info size={13} />
        推荐路径：设计参考 → 定下风格 → ACF 规划 → 导入 WordPress → 套用参考站样式
      </div>

      {/* 当前站点的 ACF 项目 */}
      <div className="mb-3.5 flex flex-wrap items-baseline gap-3">
        <h2 className="font-bold text-[16.5px] text-ink tracking-tight">
          {activeSite ? `「${activeSite.name}」的规划项目` : "我的规划项目"}
        </h2>
        <span className="text-[12.5px] text-ink-3">按站点归档 · 切换站点后这里跟着变</span>
        <span className="ml-auto">
          <Link to="/sites">
            <Button size="sm" variant="outline">
              查看全部站点
            </Button>
          </Link>
        </span>
      </div>

      {isLoadingContexts ? (
        <div className="py-6 text-[13px] text-ink-3">加载中…</div>
      ) : siteContexts.length > 0 ? (
        <div className="flex flex-col gap-2.5">
          {siteContexts.map((ctx) => (
            <Link key={ctx.id} search={{ contextId: ctx.id }} to="/site-tools/acf">
              <div className="flex items-center gap-3.5 rounded-xl border border-border bg-card px-4.5 py-3.5 transition-all hover:border-border-strong hover:shadow-s">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-soft text-brand">
                  <Blocks size={16} />
                </span>
                <div className="min-w-0">
                  <div className="truncate font-semibold text-[14px] text-ink">{ctx.name}</div>
                  <div className="mt-0.5 text-[12px] text-ink-3">
                    {ctx.isCompleted ? "规划已完成" : "访谈进行中"}
                  </div>
                </div>
                <div className="ml-auto flex items-center gap-3.5">
                  {ctx.isCompleted ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-success-soft px-2.5 py-0.5 font-semibold text-[11.5px] text-success">
                      <CheckCircle2 size={12} />
                      已完成
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-brand-soft px-2.5 py-0.5 font-semibold text-[11.5px] text-brand-text">
                      进行中
                    </span>
                  )}
                  <span className="text-[12px] text-ink-3">
                    {formatRelativeTime(ctx.updatedAt)}
                  </span>
                  <ChevronRight className="text-ink-3" size={16} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="rounded-xl border border-border border-dashed px-6 py-10 text-center">
          <p className="mb-4 text-[13px] text-ink-3">
            {activeSite ? "这个站点还没有规划项目" : "还没有规划项目"}
          </p>
          <Button onClick={openCreateOrResume} size="sm" variant="outline">
            <Plus size={14} />
            新建第一个规划
          </Button>
        </div>
      )}

      {/* 新建项目对话框 */}
      <Dialog onOpenChange={setShowCreate} open={showCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>新建 ACF 规划</DialogTitle>
          </DialogHeader>
          {activeSite ? (
            <p className="text-[12.5px] text-ink-3">将归档到当前站点「{activeSite.name}」</p>
          ) : null}
          <Input
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleCreate();
              }
            }}
            placeholder="项目名称（如：首页改版 · 板块字段）"
            value={newName}
          />
          <DialogFooter>
            <Button onClick={() => setShowCreate(false)} variant="outline">
              取消
            </Button>
            <Button disabled={!newName.trim() || createContext.isPending} onClick={handleCreate}>
              {createContext.isPending ? "创建中..." : "创建"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
